import './ui/base.css';
import './ui/tokens.css';
import './ui/ui.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './ui/Toast';
import { ConfirmProvider } from './ui/Confirm';
import ReviewShell from './shell/ReviewShell';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useAppConfig } from './lib/appConfig';
import PrivateRoute from './components/PrivateRoute';
import { hasTalentRole, isStaff, isTalentOnly } from './shell/nav';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Scripts from './pages/Scripts';
import LiveProduction from './pages/LiveProduction';
import Production from './pages/Production';
import AiProduction from './pages/AiProduction';
import Calendar from './pages/Calendar';
import Scheduler from './pages/Scheduler';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import AccountSettings from './pages/AccountSettings';
import EditQueue from './pages/EditQueue';
import ScriptShareView from './pages/ScriptShareView';
import ProductionAssistant from './pages/ProductionAssistant';
import Qual from './pages/Qual';
import TalentDashboard from './pages/TalentDashboard';
import TalentAvailability from './pages/TalentAvailability';
import TalentScriptView from './pages/TalentScriptView';
import TalentSetup from './pages/TalentSetup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VideoReviewPage from './pages/VideoReviewPage';
import ShareLinkViewer from './pages/ShareLinkViewer';
import FootageShareViewer from './pages/FootageShareViewer';
import AssetLibrary from './pages/AssetLibrary';
import CameraIngest from './pages/CameraIngest';
import RawFootage from './pages/RawFootage';
import Storage from './pages/Storage';
import TimeOff from './pages/TimeOff';
import NotFound from './pages/NotFound';

// HD-3 component sheet: dev builds, or any build made with REACT_APP_UI_SHEET=true (the dev container).
// Production builds without the flag do not mount the route; the lazy chunk is emitted but never requested.
const UI_SHEET = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_UI_SHEET === 'true';
const UiSheet = UI_SHEET ? lazy(() => import('./ui/sheet/UiSheet')) : null;

// Thin wrapper: must be logged in, but does NOT render the sidebar
function AuthRequired({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading…</div>;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

// HD-9 0c: routes gated as the API gates them. A viewer the API would refuse is sent to their own landing page and the
// page never mounts, so it makes no call. Talent pages need the Talent role; the /ui sheet is for staff only.
function TalentOnly({ children }) {
  const { user } = useAuth();
  return hasTalentRole(user) ? children : <Navigate to="/" replace />;
}
function StaffOnly({ children }) {
  const { user } = useAuth();
  return isStaff(user) ? children : <Navigate to="/" replace />;
}
// SEC-1: the video review page is staff and client only; a talent-only account goes to /talent before it mounts.
function NotTalentOnly({ children }) {
  const { user } = useAuth();
  return isTalentOnly(user) ? <Navigate to="/talent" replace /> : children;
}

// Smart default redirect — Talent-only users go to their portal; dual-role
// users (Talent + any staff role) default to the staff dashboard.
function DefaultRedirect() {
  const { user } = useAuth();
  if (isTalentOnly(user)) return <Navigate to="/talent" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  // AI Production is deployment-flagged; with the flag off the /ai route is
  // simply not mounted (the sidebar link is hidden in Sidebar.jsx).
  const { aiProductionEnabled } = useAppConfig();
  return (
    <BrowserRouter>
      <AuthProvider>
       <ToastProvider>
       <ConfirmProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/share/script/:token" element={<ReviewShell tier="light" tierKey="script"><ScriptShareView /></ReviewShell>} />
          <Route path="/talent-setup/:token" element={<TalentSetup />} />
          <Route path="/share/footage/:token" element={<ReviewShell tierKey="footage"><FootageShareViewer /></ReviewShell>} />
          <Route path="/share/:token" element={<ReviewShell><ShareLinkViewer /></ReviewShell>} />
          <Route path="/change-password" element={
            <AuthRequired><ChangePassword /></AuthRequired>
          } />
          <Route path="/review/:videoAssetId" element={
            <AuthRequired><NotTalentOnly><ReviewShell><VideoReviewPage /></ReviewShell></NotTalentOnly></AuthRequired>
          } />
          {UI_SHEET && <Route path="/ui" element={
            <AuthRequired><StaffOnly><Suspense fallback={<div className="app-loading">Loading…</div>}><UiSheet /></Suspense></StaffOnly></AuthRequired>
          } />}
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/scripts" element={<Scripts />} />
            <Route path="/qual" element={<Qual />} />
            <Route path="/live" element={<LiveProduction />} />
            <Route path="/production/:id" element={<Production />} />
            {aiProductionEnabled && <Route path="/ai" element={<AiProduction />} />}
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/edit-queue" element={<EditQueue />} />
            <Route path="/scheduler" element={<Scheduler />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/assistant" element={<ProductionAssistant />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/time-off" element={<TimeOff />} />
            <Route path="/assets" element={<AssetLibrary />} />
            <Route path="/footage" element={<RawFootage />} />
            <Route path="/footage/ingest" element={<CameraIngest />} />
            <Route path="/storage" element={<Storage />} />
            {/* Talent Portal */}
            <Route path="/talent" element={<TalentOnly><TalentDashboard /></TalentOnly>} />
            <Route path="/talent/availability" element={<TalentOnly><TalentAvailability /></TalentOnly>} />
            <Route path="/talent/scripts" element={<TalentOnly><TalentScriptView /></TalentOnly>} />
            {/* HD-11.1: every other logged-in path; shell/nav.js APP_PATHS lists the ones above */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
       </ConfirmProvider>
       </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
